import _ from 'lodash'
import moment from 'moment'

import {FieldNames, normalizeFieldName} from '../../../../fields'
import {ChangeableFieldsOptionsDto, ChangeableFieldsOptionsDtoPaths} from '../../options/types'
import {
  ChangeableFieldConfiguration,
  ChangeableFieldConfigurations,
  LeadTimeChangeableFieldConfiguration
} from '../types'

/**
 * Maps API options to changeable field configurations with proper merging of duplicate fields.
 *
 * This function transforms the raw API data structure into internal field configuration format,
 * handling cases where multiple API options map to the same field name (e.g., dateTime.date and
 * dateTime.time both mapping to the dateTime field).
 *
 * @param options - Raw changeable field options from the API
 * @param fieldsMapping - Mapping configuration defining how API paths relate to field names
 * @returns Merged and validated changeable field configurations
 *
 * Business Logic:
 * - Maps each API option path to its corresponding field name
 * - Merges configurations for fields that appear multiple times in the mapping
 * - Applies conservative merge rules (any disabled field disables the entire group)
 * - Chooses earliest dates when multiple timing constraints exist
 *
 * @example
 * ```typescript
 * const options = {
 *   'shippingDate.date': { type: 'leadTime', isEnabled: true, changeableUntil: '2025-10-22T10:00:00Z' },
 *   'shippingDate.time': { type: 'leadTime', isEnabled: true, changeableUntil: '2025-10-22T12:00:00Z' }
 * }
 *
 * const mapping = [
 *   { optionsPath: 'shippingDate.date', fieldName: { field: 'dateTime', fieldPart: 'date' } },
 *   { optionsPath: 'shippingDate.time', fieldName: { field: 'dateTime', fieldPart: 'time' } }
 * ]
 *
 * // Results in single merged configuration for dateTime field with earliest date
 * ```
 */
export const mapChangeableFieldsConfiguration = (
  options: ChangeableFieldsOptionsDto,
  fieldsMapping: {optionsPath: ChangeableFieldsOptionsDtoPaths; fieldName: FieldNames}[]
): ChangeableFieldConfigurations => {
  // Transform each mapping entry into a field configuration
  const rawConfigurations = fieldsMapping.map(({optionsPath, fieldName}) => {
    const fieldOptions = _.get(options, optionsPath)

    // If API doesn't provide options for this path, disable the field
    if (!fieldOptions) {
      return {fieldName, isEnabled: false} as const
    }

    // Combine field name with API options
    return {fieldName, ...fieldOptions}
  })

  return mergeChangeableFieldConfigurations(rawConfigurations)
}

/**
 * Merges changeable field configurations that have the same field identity.
 *
 * Multiple API options can map to the same logical field (e.g., date and time components
 * of a dateTime field). This function groups configurations by field identity and applies
 * merge rules to create a single configuration per unique field.
 *
 * @param configurations - Raw configurations before merging
 * @returns Merged configurations with one entry per unique field
 *
 * Merge Strategy:
 * - Group configurations by field + fieldPart combination
 * - Single configuration: Pass through unchanged
 * - Multiple configurations: Apply business rules for merging
 */
const mergeChangeableFieldConfigurations = (
  configurations: ChangeableFieldConfigurations
): ChangeableFieldConfigurations => {
  /**
   * Creates a unique key for grouping field configurations.
   * Uses field + fieldPart to distinguish between field components (e.g., dateTime.date vs dateTime.time).
   */
  const createFieldKey = (fieldName: FieldNames) => {
    const {field, fieldPart} = normalizeFieldName(fieldName)
    return fieldPart ? `${field}.${fieldPart}` : field
  }

  // Group configurations by their field identity
  const fieldGroups = new Map<string, typeof configurations>()
  for (const config of configurations) {
    const fieldKey = createFieldKey(config.fieldName)

    if (!fieldGroups.has(fieldKey)) {
      fieldGroups.set(fieldKey, [])
    }

    fieldGroups.get(fieldKey)!.push(config)
  }

  // Process each group to create merged configurations
  const mergedConfigurations: ChangeableFieldConfigurations = []
  for (const [, fieldConfigurations] of fieldGroups) {
    if (fieldConfigurations.length === 1) {
      // Single configuration - no merging needed
      mergedConfigurations.push(fieldConfigurations[0])
      continue
    }

    // Multiple configurations for same field - apply merge business rules
    const mergedConfiguration = mergeFieldConfigurations(
      fieldConfigurations[0].fieldName,
      fieldConfigurations
    )
    mergedConfigurations.push(mergedConfiguration)
  }

  return mergedConfigurations
}

/**
 * Merges multiple configurations for the same field using conservative business rules.
 *
 * When multiple API options map to the same field, we need to determine the final
 * configuration state. The merge applies these business rules:
 *
 * 1. Disabled Override: If any configuration is disabled, the entire field is disabled
 * 2. Type Merging: If all enabled configs are 'lifeCycle', return lifeCycle configuration
 * 3. Earliest Deadline: For 'leadTime' configs, use the most restrictive (earliest) timing constraints
 * 4. Safety First: Conservative approach protects against unintended changes
 *
 * @param fieldName - The field name being merged
 * @param configurations - All configurations for this field
 * @returns Single merged configuration representing the field's final state
 */
const mergeFieldConfigurations = (
  fieldName: FieldNames,
  configurations: ChangeableFieldConfiguration[]
): ChangeableFieldConfiguration & {fieldName: FieldNames} => {
  // Conservative rule: Any disabled configuration disables the entire field
  // This prevents partial field updates that could cause data inconsistency
  if (configurations.some((config) => !config.isEnabled)) {
    return {fieldName, isEnabled: false}
  }

  // All configurations are enabled - merge their timing constraints
  const enabledConfigurations = configurations.filter((config) => config.isEnabled)

  // Check if all are lifeCycle type - if so, return lifeCycle configuration
  const allLifeCycle = enabledConfigurations.every((config) => config.type === 'lifeCycle')
  if (allLifeCycle) {
    return {
      fieldName,
      type: 'lifeCycle',
      isEnabled: true
    }
  }

  // Filter to leadTime configurations only and merge their timing constraints
  const leadTimeConfigurations = enabledConfigurations.filter(
    (config) => config.type === 'leadTime'
  ) as LeadTimeChangeableFieldConfiguration[]

  // If we have any leadTime configs, use the most restrictive (earliest) deadlines
  if (leadTimeConfigurations.length > 0) {
    return {
      fieldName,
      type: 'leadTime',
      isEnabled: true,
      // Use the earliest (most restrictive) changeable deadline
      changeableUntil: findEarliestDate(
        leadTimeConfigurations.map((config) => config.changeableUntil)
      )!,
      // Use the earliest auto-confirmable deadline (if any exist)
      autoConfirmableUntil: findEarliestDate(
        leadTimeConfigurations.map((config) => config.autoConfirmableUntil)
      )!
    }
  }

  // Fallback: return lifeCycle (shouldn't happen, but TypeScript needs this)
  return {
    fieldName,
    type: 'lifeCycle',
    isEnabled: true
  }
}

/**
 * Finds the earliest date from a collection of ISO date strings.
 *
 * Uses moment.js for reliable date comparison across different formats and timezones.
 * This function is critical for determining the most restrictive timing constraints
 * when merging field configurations.
 *
 * @param dateStrings - Array of ISO date strings to compare
 * @returns The earliest date string, or undefined if array is empty
 *
 * Implementation Notes:
 * - Uses moment.utc() to ensure consistent timezone handling
 * - Handles empty arrays gracefully
 * - Maintains original string format for API compatibility
 *
 * @example
 * ```typescript
 * findEarliestDate(['2025-10-22T10:00:00Z', '2025-10-22T08:00:00Z'])
 * // Returns: '2025-10-22T08:00:00Z'
 * ```
 */
const findEarliestDate = (dateStrings: string[]): string | undefined => {
  if (dateStrings.length === 0) {
    return undefined
  }

  return dateStrings.reduce((earliestDate, currentDate) => {
    return moment.utc(currentDate).isBefore(moment.utc(earliestDate)) ? currentDate : earliestDate
  })
}
